<template>
  <section class="offer-products" :aria-labelledby="headingId" :style="backgroundStyle">
    <header class="offer-products__header">
      <div>
        <span class="offer-products__eyebrow"><b-icon icon="tag-fill" aria-hidden="true" /> OFERTAS</span>
        <h2 :id="headingId">{{ title || "Ofertas para aprovechar" }}</h2>
        <p>{{ text || "Descubre productos con promociones activas." }}</p>
      </div>
      <span v-if="!loading && products.length" class="offer-products__count">
        {{ products.length }} {{ products.length === 1 ? "producto" : "productos" }}
      </span>
    </header>

    <div v-if="loading" class="offer-products__loading" role="status">
      <b-spinner small aria-hidden="true" /> Cargando ofertas...
    </div>
    <div v-else-if="loadError" class="offer-products__empty" role="status">
      <b-icon icon="exclamation-circle" aria-hidden="true" />
      <strong>No pudimos cargar las ofertas.</strong>
      <button type="button" @click="load">Reintentar</button>
    </div>
    <div v-else-if="!products.length" class="offer-products__empty" role="status">
      <b-icon icon="tag" aria-hidden="true" />
      <strong>No hay ofertas disponibles por ahora.</strong>
      <p>Vuelve pronto para conocer las próximas promociones.</p>
    </div>
    <div v-else class="offer-products__grid">
      <div v-for="product in products" :key="product._id" class="offer-products__item">
        <single-product :data="product" product-template="category-showcase" />
      </div>
    </div>
  </section>
</template>

<script>
const singleProduct = () => import(/* webpackChunkName: "singleproduct" */ "./singleProduct.vue");

export default {
  components: { singleProduct },
  props: {
    title: { type: String, default: "" },
    text: { type: String, default: "" },
    offerProductsLimit: { type: [Number, String], default: 6 },
    bgColor: { type: String, default: "" },
    src: { type: String, default: "" },
    offerTextColor: { type: String, default: "" },
  },
  data() {
    return { offers: [], loading: true, loadError: false };
  },
  computed: {
    headingId() {
      return `offer-products-heading-${this._uid}`;
    },
    webName() {
      return this.$store.state.info && this.$store.state.info.webName;
    },
    limit() {
      return Math.max(1, Math.min(12, Math.floor(Number(this.offerProductsLimit) || 6)));
    },
    products() {
      return this.offers.slice(0, this.limit);
    },
    backgroundStyle() {
      const color = this.bgColor === "transparent" || /^#[0-9a-f]{3,8}$/i.test(this.bgColor) ? this.bgColor : "#fff";
      const style = /^#[0-9a-f]{3,8}$/i.test(this.offerTextColor) ? { "--offers-text": this.offerTextColor } : {};
      if (!this.src) return this.bgColor ? { ...style, background: color } : style;
      const url = String(this.src).replace(/["\\\n\r]/g, "");
      return { ...style, backgroundColor: color, backgroundImage: `url("${url}")`, backgroundPosition: "center", backgroundSize: "cover" };
    },
  },
  mounted() {
    this.load();
  },
  watch: {
    webName(next, previous) {
      if (next && next !== previous) this.load();
    },
  },
  methods: {
    async load() {
      if (!this.webName) {
        this.loading = false;
        return;
      }
      this.loading = true;
      this.loadError = false;
      try {
        const response = await this.$http.get(`${this.webName}/product?offers=1&limit=200`);
        this.offers = Array.isArray(response.data.data) ? response.data.data : [];
      } catch (error) {
        this.offers = [];
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.offer-products {
  --offers-accent: var(--ticketapp-primary-color, #e43c42);
  padding: clamp(22px, 4vw, 52px) clamp(12px, 3vw, 36px);
  border-radius: 24px;
  background: radial-gradient(circle at 100% 0%, color-mix(in srgb, var(--offers-accent) 10%, white), transparent 35%), #fff;
}
.offer-products__header { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; margin-bottom: 24px; }
.offer-products__eyebrow { display: inline-flex; align-items: center; gap: 8px; color: var(--offers-text, var(--offers-accent)); font-size: 0.72rem; font-weight: 850; letter-spacing: 0.13em; }
.offer-products__header h2 { margin: 7px 0 5px; color: var(--offers-text, #26384b); font-size: clamp(1.7rem, 3vw, 2.5rem); font-weight: 850; letter-spacing: -0.04em; }
.offer-products__header p { margin: 0; color: var(--offers-text, #718096); }
.offer-products__count { flex: 0 0 auto; padding: 7px 12px; border-radius: 999px; background: color-mix(in srgb, var(--offers-accent) 12%, white); color: var(--offers-text, var(--offers-accent)); font-size: 0.8rem; font-weight: 750; }
.offer-products__grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: clamp(14px, 2vw, 28px); }
.offer-products__item { min-width: 0; }
.offer-products__loading, .offer-products__empty { display: flex; min-height: 170px; align-items: center; justify-content: center; gap: 9px; color: var(--offers-text, #718096); }
.offer-products__empty { flex-direction: column; text-align: center; }
.offer-products__empty > svg { color: var(--offers-accent); font-size: 1.7rem; }
.offer-products__empty strong { color: var(--offers-text, #26384b); }
.offer-products__empty p { margin: 0; }
.offer-products__empty button { padding: 7px 15px; border: 1px solid var(--offers-accent); border-radius: 8px; background: #fff; color: var(--offers-accent); }
@media (max-width: 991.98px) { .offer-products__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 575.98px) { .offer-products__grid { grid-template-columns: minmax(0, 1fr); } .offer-products__header { align-items: flex-start; flex-direction: column; } }
</style>
